import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, CircularProgress, Snackbar, Tab, Tabs, Typography } from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { useTheme } from '../../context/ThemeContext';
import platformMcpService from '../../services/platformMcpService';
import mcpKeysService from '../../services/mcpKeysService';
import { describeApiError } from '../../utils/apiError';
import PlatformMcpActivityTable from '../shared/PlatformMcpActivity/PlatformMcpActivityTable';
import ConnectTab from './ConnectTab';
import CreateKeyDialog from './CreateKeyDialog';
import MyKeysTab from './MyKeysTab';
import ToolsTab from './ToolsTab';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import pageStyles from '../MyLLMKeys/MyLLMKeys.module.scss';
import adminStyles from '../PlatformAdmin/PlatformAdmin.module.scss';

const TABS = [
  { value: 'connect', label: 'Connect' },
  { value: 'keys', label: 'My keys' },
  { value: 'activity', label: 'My activity' },
  { value: 'tools', label: 'What it can do' },
];

/**
 * "Connect Platform MCP" — use Quadroid from AI apps with a personal
 * connection key (spec screen A).
 *
 * Page status: loading | ready | notReady (server off / not set up) |
 * noAccess (403, no `platform_mcp`) | error. My keys works in every status,
 * so a user who lost access can still revoke keys.
 */
const ConnectPlatformMcp = () => {
  const { isDarkMode } = useTheme();
  const [tab, setTab] = useState('connect');

  const [status, setStatus] = useState('loading');
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);

  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [keysError, setKeysError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  // The last create response's `connect`, filled in with the real key. Memory
  // only: it can't be fetched again and must not outlive the page.
  const [created, setCreated] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const notify = useCallback(
    (message, severity = 'info') => setSnackbar({ open: true, message, severity }),
    []
  );

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: { mode: isDarkMode ? 'dark' : 'light', primary: { main: '#e36250' } },
        typography: { fontFamily: '"Poppins", "sans-serif"' },
      }),
    [isDarkMode]
  );

  const loadPage = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    try {
      const res = await platformMcpService.getPage();
      setData(res || null);
      setStatus(res?.server_ready ? 'ready' : 'notReady');
    } catch (err) {
      if (err?.status === 403) {
        setStatus('noAccess');
      } else {
        setLoadError(describeApiError(err, "Couldn't load Platform MCP."));
        setStatus('error');
      }
    }
  }, []);

  const loadKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      setKeysError(null);
      const res = await mcpKeysService.listKeys();
      setKeys(Array.isArray(res?.keys) ? res.keys : []);
    } catch (err) {
      setKeysError(describeApiError(err, "Couldn't load your keys."));
    } finally {
      setKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage();
    loadKeys();
  }, [loadPage, loadKeys]);

  const handleCreated = (res) => {
    if (res?.connect) setCreated({ connect: res.connect, name: res.name || '' });
    loadKeys();
  };

  const blocked = status === 'noAccess';

  return (
    <ThemeProvider theme={muiTheme}>
      <Box className={pageStyles.layout}>
        <Box className={pageStyles.inner}>
          <Box className={pageStyles.header}>
            <Box className={pageStyles.titleSection}>
              <Typography variant="h4" component="h1">
                {PLATFORM_MCP_COPY.menu}
              </Typography>
              <Typography variant="body2">{PLATFORM_MCP_COPY.subtitle}</Typography>
            </Box>
          </Box>

          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            className={adminStyles.consoleTabs}
            TabIndicatorProps={{ style: { backgroundColor: '#e36250' } }}
            variant="scrollable"
            scrollButtons="auto"
          >
            {TABS.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} className={adminStyles.tab} />
            ))}
          </Tabs>

          {tab !== 'keys' && status === 'loading' ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
              <CircularProgress color="primary" />
            </Box>
          ) : (
            <>
              {tab === 'connect' && (
                <ConnectTab
                  status={status}
                  data={data}
                  created={created}
                  loadError={loadError}
                  onCreate={() => setCreateOpen(true)}
                  onRetry={loadPage}
                />
              )}
              {tab === 'keys' && (
                <MyKeysTab
                  keys={keys}
                  loading={keysLoading}
                  error={keysError}
                  onReload={loadKeys}
                  notify={notify}
                />
              )}
              {tab === 'activity' &&
                (blocked ? (
                  <Alert severity="warning">{PLATFORM_MCP_COPY.noAccess}</Alert>
                ) : (
                  <PlatformMcpActivityTable scope="user" />
                ))}
              {tab === 'tools' &&
                (blocked ? (
                  <Alert severity="warning">{PLATFORM_MCP_COPY.noAccess}</Alert>
                ) : (
                  <ToolsTab tools={Array.isArray(data?.tools) ? data.tools : []} />
                ))}
            </>
          )}
        </Box>
      </Box>

      <CreateKeyDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={handleCreated}
        onNoAccess={() => setStatus('noAccess')}
        onGoToKeys={() => setTab('keys')}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default ConnectPlatformMcp;
